/**
 * Optimized clustering utilities for MapScreen
 * Implements spatial indexing and viewport-based filtering
 */

// Simple spatial grid index for fast lookups
class SpatialGrid {
  constructor(cellSize = 0.01) {
    this.cellSize = cellSize;
    this.grid = new Map();
  }

  _getCellKey(lat, lng) {
    const latCell = Math.floor(lat / this.cellSize);
    const lngCell = Math.floor(lng / this.cellSize);
    return `${latCell},${lngCell}`;
  }

  insert(restroom) {
    const key = this._getCellKey(restroom.latitude, restroom.longitude);
    if (!this.grid.has(key)) {
      this.grid.set(key, []);
    }
    this.grid.get(key).push(restroom);
  }

  query(bounds) {
    const results = [];
    const { north, south, east, west } = bounds;
    
    const startLat = Math.floor(south / this.cellSize);
    const endLat = Math.floor(north / this.cellSize);
    const startLng = Math.floor(west / this.cellSize);
    const endLng = Math.floor(east / this.cellSize);

    for (let lat = startLat; lat <= endLat; lat++) {
      for (let lng = startLng; lng <= endLng; lng++) {
        const key = `${lat},${lng}`;
        const cell = this.grid.get(key);
        if (cell) {
          results.push(...cell);
        }
      }
    }
    
    return results;
  }

  clear() {
    this.grid.clear();
  }
}

// Cluster cache with LRU eviction
class ClusterCache {
  constructor(maxSize = 20) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  _generateKey(restroomsHash, zoom, isMergeMode, boundsHash = null) {
    if (boundsHash) {
      return `${restroomsHash}-${zoom}-${isMergeMode}-${boundsHash}`;
    }
    return `${restroomsHash}-${zoom}-${isMergeMode}`;
  }

  _generateBoundsHash(bounds) {
    if (!bounds) return null;
    // Create a simple hash from bounds rounded to reduce cache misses
    const precision = 3;
    const n = bounds.north.toFixed(precision);
    const s = bounds.south.toFixed(precision);
    const e = bounds.east.toFixed(precision);
    const w = bounds.west.toFixed(precision);
    return `${n},${s},${e},${w}`;
  }

  _generateHash(restrooms) {
    // Hash based on count and first/last IDs PLUS a light content fingerprint.
    // In-place updates (e.g. a new review changing averageRating/ratingCount, or
    // an admin verification) keep the same count and first/last IDs, so without
    // the fingerprint the cache would serve stale clusters and the map icons
    // and clicked-restroom data would never reflect the new rating.
    if (restrooms.length === 0) return 'empty';
    const firstId = restrooms[0]?.id || '';
    const lastId = restrooms[restrooms.length - 1]?.id || '';
    let ratingFingerprint = 0;
    let reviewFingerprint = 0;
    let verifiedFingerprint = 0;
    for (const restroom of restrooms) {
      ratingFingerprint += Math.round(Number(restroom?.averageRating || restroom?.rating || 0) * 10);
      reviewFingerprint += Math.min(Number(restroom?.ratingCount || 0), 1000000);
      if (restroom?.verified === true) verifiedFingerprint += 1;
    }
    return `${restrooms.length}-${firstId}-${lastId}-${ratingFingerprint}-${reviewFingerprint}-${verifiedFingerprint}`;
  }

  get(restrooms, zoom, isMergeMode, bounds = null) {
    const hash = this._generateHash(restrooms);
    const boundsHash = this._generateBoundsHash(bounds);
    const key = this._generateKey(hash, zoom, isMergeMode, boundsHash);
    
    if (this.cache.has(key)) {
      // Move to end (most recently used)
      const value = this.cache.get(key);
      this.cache.delete(key);
      this.cache.set(key, value);
      return value;
    }
    return null;
  }

  set(restrooms, zoom, isMergeMode, clusters, bounds = null) {
    const hash = this._generateHash(restrooms);
    const boundsHash = this._generateBoundsHash(bounds);
    const key = this._generateKey(hash, zoom, isMergeMode, boundsHash);
    
    // Evict oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, clusters);
  }

  clear() {
    this.cache.clear();
  }
}

// Optimized clustering function
export function clusterRestrooms(restrooms, displayZoom, isMergeMode, bounds = null) {
  // Filter valid restrooms
  const filteredRestrooms = restrooms.filter(r => 
    r && r.id && 
    typeof r.latitude === 'number' && typeof r.longitude === 'number' &&
    !isNaN(r.latitude) && !isNaN(r.longitude)
  );

  // If zoom is high enough or in merge mode, no clustering needed
  if (displayZoom >= 11 || isMergeMode) {
    return filteredRestrooms.map(r => ({ ...r, type: 'restroom' }));
  }

  // If bounds provided, only cluster visible restrooms
  const restroomsToCluster = bounds 
    ? filterRestroomsByBounds(filteredRestrooms, bounds)
    : filteredRestrooms;

  // Grid-based clustering
  const gridSize = 360 / Math.pow(2, displayZoom);
  const grid = {};

  restroomsToCluster.forEach(restroom => {
    const gridX = Math.floor(restroom.latitude / gridSize);
    const gridY = Math.floor(restroom.longitude / gridSize);
    const key = `${gridX},${gridY}`;

    if (!grid[key]) {
      grid[key] = {
        id: `cluster-${key}`,
        latitude: 0,
        longitude: 0,
        restrooms: [],
        type: 'cluster',
        count: 0,
        totalRating: 0
      };
    }

    const cluster = grid[key];
    cluster.restrooms.push(restroom);
    cluster.count++;
    cluster.latitude += restroom.latitude;
    cluster.longitude += restroom.longitude;
    cluster.totalRating += (restroom.rating || 3);
  });

  return Object.values(grid).map(cluster => {
    if (cluster.count === 1) {
      return {
        ...cluster.restrooms[0],
        type: 'restroom'
      };
    }

    return {
      ...cluster,
      latitude: cluster.latitude / cluster.count,
      longitude: cluster.longitude / cluster.count,
      averageRating: cluster.totalRating / cluster.count
    };
  });
}

// Filter restrooms by map bounds
export function filterRestroomsByBounds(restrooms, bounds) {
  const { north, south, east, west } = bounds;
  
  return restrooms.filter(restroom => {
    const lat = restroom.latitude;
    const lng = restroom.longitude;
    return lat >= south && lat <= north && lng >= west && lng <= east;
  });
}

// Create spatial index from restrooms
export function createSpatialIndex(restrooms, cellSize = 0.01) {
  const index = new SpatialGrid(cellSize);
  restrooms.forEach(r => index.insert(r));
  return index;
}

// Export cache class for use in components
export { ClusterCache, SpatialGrid };
